import React, { useState, useEffect, useMemo } from "react";
import T from '../../../components/common/T';
import { useParams, useNavigate } from "react-router-dom";
import { useInventory } from "../../../context/InventoryContext";
import { useProjects } from "../../../context/ProjectContext";
import { useTasks } from "../../../context/TaskContext"; 
import { useCostSheets } from "../../../context/CostSheetContext"; 
import api from "../../../api/axiosConfig";

import OdooFormLayout from "../../../components/OdooFormLayout"; 
import SmartButton from "../../../components/SmartButton";
import { TrashIcon, ArchiveBoxArrowDownIcon, PlusIcon, CurrencyDollarIcon, ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function ScrapForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const { inventories = [], warehouses = [], createScrapOrder, updateRequisitionStatus, validateScrapRequisition } = useInventory();
  const { projects = [] } = useProjects();
  const { tasks = [] } = useTasks();
  const { costSheets = [] } = useCostSheets(); 

  const [activeTab, setActiveTab] = useState("lines");
  
  const [formData, setFormData] = useState({
    id: "",
    reference_number: "",
    status: "NEW",
    employee: "Mitchell Admin",
    department: "Management",
    company: "My Company",
    projectId: "",
    projectName: "",
    taskId: "",
    costCenterId: "", 
    requisitionDate: new Date().toISOString().split('T')[0],
    receivedDate: "",
    scrapType: "Scrap", 
    sourceWarehouseId: "", 
    sourceBin: "",         
    lines: [] 
  });

  // 🟢 DYNAMIC STAGES: Ensures the stepper looks correct even if SOLD or RECOVERED
  const STAGES = useMemo(() => {
      const base = ["NEW", "WAITING DEPARTMENT APPROVAL", "WAITING IR APPROVAL", "APPROVED"];
      if (formData.status === "SOLD") return [...base, "SOLD"];
      if (formData.status === "RECOVERED") return [...base, "RECOVERED"];
      return [...base, "RECEIVED"];
  }, [formData.status]);

  // 🟢 READ-ONLY CHECK: Locks the form if the order is fully processed
  const isReadOnly = ["RECEIVED", "SOLD", "RECOVERED"].includes(formData.status);

  const currentWarehouseBins = useMemo(() => {
    const wh = warehouses.find(w => String(w.id) === String(formData.sourceWarehouseId));
    return wh?.bins || ["General"]; 
  }, [warehouses, formData.sourceWarehouseId]);

  useEffect(() => {
    if (isNew) return;

    const loadRealData = async () => {
        try {
            const res = await api.get(`/scrap-orders/${id}`);
            const dbData = res.data?.data || res.data;

            setFormData(prev => ({
                ...prev,
                id: dbData.id,
                reference_number: dbData.reference_number || dbData.id,
                status: dbData.status || "NEW",
                employee: dbData.employee_name || dbData.employee || prev.employee,
                department: dbData.department || prev.department,
                company: dbData.company || prev.company,
                projectId: dbData.project_id || prev.projectId,
                projectName: dbData.project_name || prev.projectName,
                taskId: dbData.task_id || prev.taskId,
                costCenterId: dbData.cost_sheet_id || prev.costCenterId,
                requisitionDate: dbData.requisition_date ? new Date(dbData.requisition_date).toISOString().split('T')[0] : prev.requisitionDate,
                receivedDate: dbData.received_date ? new Date(dbData.received_date).toISOString().split('T')[0] : prev.receivedDate,
                scrapType: dbData.scrap_type || prev.scrapType,
                lines: dbData.lines ? dbData.lines.map(l => ({
                    id: l.id,
                    productId: l.product_id,
                    description: l.description || l.product_name,
                    quantity: l.quantity,
                    unit: l.unit || "Units"
                })) : prev.lines,
            }));
        } catch (e) {
            console.error("Failed to load order from database:", e);
            toast.error("Failed to load real data.");
        }
    };
    loadRealData();
  }, [id, isNew]);

  const projectTasks = useMemo(() => formData.projectId ? tasks.filter(t => String(t.projectId) === String(formData.projectId)) : [], [tasks, formData.projectId]);
  const projectCostSheets = useMemo(() => formData.projectId ? costSheets.filter(cs => String(cs.projectId) === String(formData.projectId)) : [], [costSheets, formData.projectId]);
  const availableProducts = inventories;

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleProjectChange = (e) => {
      const p = projects.find(x => String(x.id) === String(e.target.value));
      setFormData(prev => ({ 
          ...prev, projectId: e.target.value, projectName: p ? p.name : "",
          taskId: "", costCenterId: "", lines: [] 
      }));
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    
    if (field === 'productId') {
        const product = inventories.find(i => String(i.id) === String(value));
        if (product) {
            newLines[index].description = product.item_name || product.itemName || product.name;
            newLines[index].unit = product.unit || "Units";
            
            if (product.locations && product.locations.length > 0) {
                const bestLocation = [...product.locations].sort((a, b) => b.qty - a.qty)[0];
                newLines[index].sourceBin = bestLocation.bin;
                setFormData(prev => ({
                    ...prev,
                    sourceWarehouseId: bestLocation.warehouseId,
                    sourceBin: bestLocation.bin,
                    lines: newLines
                }));
                return; 
            }
        }
    }
    setFormData(prev => ({ ...prev, lines: newLines }));
  };

  const addLine = () => setFormData(prev => ({ ...prev, lines: [...prev.lines, { id: Date.now(), productId: "", description: "", quantity: 1, unit: "Units" }] }));
  const removeLine = (index) => setFormData(prev => ({ ...prev, lines: prev.lines.filter((_, i) => i !== index) }));

  const handleConfirm = async () => {
    if (!formData.projectId) return toast.error("Please select a Construction Project.");
    if (!formData.lines || formData.lines.length === 0) return toast.error("Please add at least one item to scrap.");
    if (!formData.sourceWarehouseId || !formData.sourceBin) return toast.error("Please select a Source Warehouse and Bin in the Picking Details tab.");

    if (isNew) {
        const firstLine = formData.lines[0];
        const payloadToSave = { 
            ...formData, 
            warehouseId: formData.sourceWarehouseId,
            binName: formData.sourceBin,
            productId: firstLine?.productId,
            quantity: Number(firstLine?.quantity)
        };
        
        const res = await createScrapOrder({ ...payloadToSave, status: "WAITING DEPARTMENT APPROVAL" });
        if (res.success) {
            toast.success("Draft Created & Confirmed");
            navigate(`/admin/material/scrap/view/${res.id}`, { replace: true });
        } else {
            toast.error(res.message || "Server Error: Failed to save record.");
        }
    } else {
        const res = await updateRequisitionStatus(id, "WAITING DEPARTMENT APPROVAL");
        if (res.success) {
            setFormData(prev => ({ ...prev, status: "WAITING DEPARTMENT APPROVAL" }));
            toast.success("Confirmed! Waiting for Department Approval.");
        } else {
            toast.error("Failed to update status on server.");
        }
    }
  };

  const handleApprove = async () => {
     let nextStatus = "WAITING IR APPROVAL";
     if (formData.status === "WAITING IR APPROVAL") nextStatus = "APPROVED";

     const res = await updateRequisitionStatus(id, nextStatus);
     if (res.success) {
         setFormData(prev => ({ ...prev, status: nextStatus }));
         toast.success(`${nextStatus === "APPROVED" ? "IR Approved" : "Department Approved"}.`);
     } else {
         toast.error("Failed to approve.");
     }
  };

  const handleStoreScrap = async () => {
    const res = await validateScrapRequisition(id);
    if (res.success) {
        const today = new Date().toISOString().split('T')[0];
        setFormData(prev => ({ ...prev, status: "RECEIVED", receivedDate: today }));
        toast.success("Validated! Material has been moved successfully.");
    } else {
        toast.error(res.message || "Validation failed.");
    }
  };

  const renderActions = () => (
    <>
      {formData.status === "NEW" && <button type="button" onClick={handleConfirm} className="bg-purple-700 text-white px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-purple-800 transition shadow"><T>Confirm</T></button>}
      {(formData.status === "WAITING DEPARTMENT APPROVAL" || formData.status === "WAITING IR APPROVAL") && <button type="button" onClick={handleApprove} className="bg-purple-700 text-white px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-purple-800 transition shadow"><T>Approve</T></button>}
      {formData.status === "APPROVED" && <button type="button" onClick={handleStoreScrap} className="bg-purple-700 text-white px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-purple-800 transition shadow">Validate</button>}
      
      {/* 🟢 DYNAMIC BUTTONS FOR COMPLETED ORDERS */}
      {formData.status === "RECEIVED" && (
          <>
            <button type="button" onClick={() => navigate(`/admin/material/scrap/reuse?orderId=${formData.id}`)} className="bg-green-600 text-white px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-green-700 transition shadow flex items-center gap-1"><ArrowUturnLeftIcon className="w-4 h-4"/> Recover</button>
            <button type="button" onClick={() => navigate(`/admin/material/scrap/sell?orderId=${formData.id}`)} className="bg-blue-600 text-white px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-blue-700 transition shadow flex items-center gap-1"><CurrencyDollarIcon className="w-4 h-4"/> Sell</button>
          </>
      )}

      <button type="button" onClick={() => navigate('/admin/material/scrap')} className="ml-2 border border-gray-300 bg-white text-gray-700 px-3 py-1.5 text-xs font-bold uppercase rounded hover:bg-gray-50 transition"><T>Back</T></button>
    </>
  );

  return (
    <>
      <div className="pt-24">
        <OdooFormLayout
            title={formData.reference_number || (isNew ? "New Scrap" : `Order #${formData.id}`)} 
            subTitle={formData.projectName || "Scrap Order"}
            status={formData.status}
            stages={STAGES}
            actions={renderActions()}
            smartButtons={isReadOnly && <SmartButton label={formData.status === "SOLD" ? "Sale Record" : formData.status === "RECOVERED" ? "Recovery Record" : "Stock History"} value="1" icon={ArchiveBoxArrowDownIcon} />}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 mb-8">
                {/* LEFT COLUMN */}
                <div className="space-y-3">
                    <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Employee</T></label><input name="employee" value={formData.employee} onChange={handleChange} className="flex-1 bg-transparent border-b border-gray-300 focus:border-purple-600 outline-none text-sm py-1" disabled={isReadOnly} /></div>
                    <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Department</T></label><input name="department" value={formData.department} onChange={handleChange} className="flex-1 bg-transparent border-b border-gray-300 focus:border-purple-600 outline-none text-sm py-1" disabled={isReadOnly} /></div>
                    <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Company</T></label><input name="company" value={formData.company} onChange={handleChange} className="flex-1 bg-transparent border-b border-gray-300 focus:border-purple-600 outline-none text-sm py-1" disabled={isReadOnly} /></div>
                    <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Construction Project</T></label><select name="projectId" value={formData.projectId} onChange={handleProjectChange} className="flex-1 bg-transparent border-b border-gray-300 focus:border-purple-600 outline-none text-sm py-1" disabled={isReadOnly}><option value=""><T>Select Project...</T></option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                    <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Task / Job Order</T></label><select name="taskId" value={formData.taskId} onChange={(e) => setFormData({...formData, taskId: e.target.value})} className="flex-1 bg-transparent border-b border-gray-300 focus:border-purple-600 outline-none text-sm py-1" disabled={isReadOnly}><option value=""><T>Select Task...</T></option>{projectTasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}</select></div>
                    <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Job Cost Center</T></label><select name="costCenterId" value={formData.costCenterId} onChange={(e) => setFormData({...formData, costCenterId: e.target.value})} className="flex-1 bg-transparent border-b border-gray-300 focus:border-purple-600 outline-none text-sm py-1" disabled={isReadOnly}><option value=""><T>Select Cost Sheet...</T></option>{projectCostSheets.map(cs => <option key={cs.id} value={cs.id}>{cs.name}</option>)}</select></div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="space-y-3">
                    <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Requisition Date</T></label><input type="date" name="requisitionDate" value={formData.requisitionDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-gray-300 focus:border-purple-600 outline-none text-sm py-1" disabled={isReadOnly} /></div>
                    <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Received Date</T></label><input type="date" value={formData.receivedDate} readOnly className="flex-1 bg-transparent border-b border-gray-300 focus:border-purple-600 outline-none text-sm py-1 text-purple-700" /></div>
                    <div className="flex items-center"><label className="w-40 text-sm font-bold text-gray-700"><T>Action</T></label><span className="text-sm text-gray-700 font-medium"><T>Scrap Order</T></span></div>

                    <div className="flex items-start">
                        <label className="w-40 text-sm font-bold text-gray-700 mt-1"><T>Scrap Type</T></label>
                        <div className="flex flex-col gap-1.5 mt-1">
                            <label className="flex items-center gap-2 text-sm text-gray-700 font-medium cursor-pointer"><input type="radio" name="scrapType" value="Store" checked={formData.scrapType === "Store"} onChange={handleChange} className="accent-purple-600" disabled={isReadOnly} /><T> Store (Return)</T></label>
                            <label className="flex items-center gap-2 text-sm text-gray-700 font-medium cursor-pointer"><input type="radio" name="scrapType" value="Scrap" checked={formData.scrapType === "Scrap"} onChange={handleChange} className="accent-purple-600" disabled={isReadOnly} /><T> Scrap (Discard)</T></label>
                        </div>
                    </div>
                </div>
            </div>

            {/* TABS */}
            <div className="mt-8">
                <div className="flex border-b border-gray-200 mb-4 gap-6">
                    <button type="button" onClick={() => setActiveTab("lines")} className={`pb-2 px-1 text-sm font-bold uppercase border-b-2 transition ${activeTab === "lines" ? "text-purple-700 border-purple-700" : "text-gray-500 border-transparent hover:text-gray-700"}`}><T>Scrap Requisitions Lines</T></button>
                    <button type="button" onClick={() => setActiveTab("picking")} className={`pb-2 px-1 text-sm font-bold uppercase border-b-2 transition ${activeTab === "picking" ? "text-purple-700 border-purple-700" : "text-gray-500 border-transparent hover:text-gray-700"}`}><T>Picking Details</T></button>
                    <button type="button" onClick={() => setActiveTab("other")} className={`pb-2 px-1 text-sm font-bold uppercase border-b-2 transition ${activeTab === "other" ? "text-purple-700 border-purple-700" : "text-gray-500 border-transparent hover:text-gray-700"}`}><T>Other Information</T></button>
                </div>

                {/* 1. LINES TAB */}
                {activeTab === "lines" && (
                    <>
                        <table className="w-full text-left text-sm text-gray-700">
                            <thead className="bg-gray-50 uppercase text-xs font-bold text-gray-500 border-b border-gray-200">
                                <tr>
                                    <th className="px-3 py-2 w-1/3"><T>Product</T></th>
                                    <th className="px-3 py-2 w-1/3"><T>Description</T></th>
                                    <th className="px-3 py-2 text-right"><T>Quantity</T></th>
                                    <th className="px-3 py-2"><T>Unit</T></th>
                                    <th className="px-3 py-2 w-10"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {formData.lines.map((line, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-3 py-2">
                                            <select value={line.productId} onChange={(e) => handleLineChange(idx, 'productId', e.target.value)} className="w-full bg-transparent outline-none focus:text-purple-700" disabled={isReadOnly}>
                                                <option value=""><T>Select Product...</T></option>
                                                {availableProducts.map(i => <option key={i.id} value={i.id}>{i.item_name || i.itemName || i.name}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-3 py-2"><input value={line.description} onChange={(e) => handleLineChange(idx, 'description', e.target.value)} className="w-full bg-transparent outline-none" disabled={isReadOnly} /></td>
                                        <td className="px-3 py-2 text-right"><input type="number" value={line.quantity} onChange={(e) => handleLineChange(idx, 'quantity', e.target.value)} className="w-20 text-right bg-transparent outline-none border-b border-transparent focus:border-purple-600" disabled={isReadOnly} /></td>
                                        <td className="px-3 py-2 text-gray-500">{line.unit}</td>
                                        <td className="px-3 py-2 text-center">{!isReadOnly && <button type="button" onClick={() => removeLine(idx)} className="text-gray-300 hover:text-red-500"><TrashIcon className="w-4 h-4" /></button>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!isReadOnly && <button type="button" onClick={addLine} className="mt-3 text-purple-700 text-xs font-bold uppercase hover:underline flex items-center gap-1"><PlusIcon className="w-3.5 h-3.5"/> <T>Add a line</T></button>}
                    </>
                )}

                {/* 2. PICKING DETAILS TAB */}
                {activeTab === "picking" && (
                    <div className="grid grid-cols-2 gap-12 text-sm text-gray-800 p-4 border border-gray-100 rounded bg-gray-50">
                        <div className="space-y-4">
                            <h4 className="font-bold text-gray-500 uppercase text-xs border-b pb-1"><T>Source</T></h4>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-gray-500"><T>Warehouse</T></span>
                                <select 
                                    className="font-medium bg-transparent outline-none text-right focus:text-purple-700 w-48"
                                    value={formData.sourceWarehouseId}
                                    onChange={e => setFormData(p => ({...p, sourceWarehouseId: e.target.value, sourceBin: ""}))}
                                    disabled={isReadOnly}
                                >
                                    <option value="">Select Warehouse...</option>
                                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-500"><T>Bin / Shelf</T></span>
                                <select 
                                    className="font-medium bg-transparent outline-none text-right focus:text-purple-700 w-48"
                                    value={formData.sourceBin}
                                    onChange={e => setFormData(p => ({...p, sourceBin: e.target.value}))}
                                    disabled={isReadOnly || !formData.sourceWarehouseId}
                                >
                                    <option value="">Select Bin...</option>
                                    {currentWarehouseBins.map(b => <option key={b} value={b}>{b}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <h4 className="font-bold text-gray-500 uppercase text-xs border-b pb-1"><T>Destination</T></h4>
                            <div className="flex justify-between">
                                <span className="text-gray-500"><T>Location</T></span>
                                <span className="font-medium text-gray-900">{formData.scrapType === "Scrap" ? "Virtual/Scrap" : "WH/Stock"}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* 3. OTHER INFO TAB */}
                {activeTab === "other" && (
                    <div className="p-4 text-sm text-gray-500 italic bg-gray-50 rounded-lg border border-gray-100">
                        <p>Created by: <span className="font-semibold text-gray-700">{formData.employee}</span> on {new Date(formData.requisitionDate).toLocaleDateString()}</p>
                        <p>Database ID: <span className="font-semibold text-gray-700">{formData.id}</span></p>
                        <p>Status: <span className="font-semibold text-gray-700">{formData.status}</span></p>
                        {formData.receivedDate && <p className="text-green-600 font-bold mt-2">Processed on: {new Date(formData.receivedDate).toLocaleString()}</p>}
                    </div>
                )}
            </div>
        </OdooFormLayout>
      </div>
    </>
  );
}